import { sortBy } from "lodash";
import API from "serviceshift-ui/api-client";

import { EmailRecipient } from "@/EmailQuoteOrInvoice/types";
import { EmailTemplateCustomization } from "@/EmailTemplates/emailTemplate";

export default {
  approveQuote({ dispatch }, { url, note }) {
    return API.makeRequest(url, {
      method: "POST",
      data: {
        override_reason: note
      }
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  addItem({ dispatch }, params) {
    const { url, payload } = params;
    return dispatch("confirmQuoteChange").then(() => {
      return API.makeRequest(url, {
        method: "POST",
        data: payload
      }).then((response) => {
        dispatch("refreshJobAndQuote", response.data);
      });
    });
  },
  createQuote(_, { customerId, data }) {
    return API.makeRequest(`/v2/customers/${customerId}/quotes`, {
      method: "POST",
      data,
      params: {
        item_grouping: 1
      }
    });
  },
  createJobQuote({ dispatch }, { create_url, data }) {
    return API.makeRequest(create_url, {
      method: "POST",
      data
    }).then((response) => {
      const quote = response.data;
      dispatch("fetchJob", {
        jobId: quote.job_id
      });
      return quote;
    });
  },
  callUpdateItem({ dispatch }, item) {
    return API.makeRequest(item.links.line_item_update, {
      method: "PUT",
      data: item
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  fetchDiscounts({ commit }) {
    return API.quote.getDiscounts().then((res) => {
      commit("replace", {
        state: "discounts",
        data: res.data
      });
    });
  },
  fetchQuote(_, { quoteId, customerId }) {
    return API.makeRequest(`/v2/customers/${customerId}/quotes/${quoteId}`, {
      method: "GET",
      params: {
        item_grouping: 1
      }
    });
  },
  fetchTasks({ commit }, params) {
    return API.serviceCategories.getTasks(params).then((res) => {
      commit("replace", {
        state: "tasks",
        data: res.data
      });
    });
  },
  removeItem({ dispatch }, url) {
    dispatch("confirmQuoteChange").then(() => {
      return API.makeRequest(url, {
        method: "DELETE"
      }).then((response) => {
        dispatch("refreshJobAndQuote", response.data);
      });
    });
  },
  setQuoteOwnerAndCommissions({ state }, { quoteId, data }) {
    return API.makeRequest(`/v2/quotes/${quoteId}/commissions`, {
      method: "PUT",
      data,
      params: { item_grouping: 1 }
    }).then((res) => {
      const { job } = state;
      if (job && job.id) {
        // Update quote in job quotes
        job.quotes = job.quotes.map((quote) => {
          if (quote.id === quoteId) {
            quote = res.data;
          }
          return quote;
        });
      }
    });
  },
  swapMembershipProgram({ dispatch }, quote) {
    dispatch("confirmQuoteChange").then(() => {
      return API.makeRequest(quote.links.quote_pricing, {
        method: "PUT",
        data: {
          actual_membership_program_id: quote.comparison_membership_program_id,
          comparison_membership_program_id: quote.actual_membership_program_id
        }
      }).then((response) => {
        dispatch("refreshJobAndQuote", response.data);
      });
    });
  },
  updateItem({ dispatch }, item) {
    dispatch("confirmQuoteChange").then(() => {
      return dispatch("callUpdateItem", item);
    });
  },
  updateItemNoConfirm({ dispatch }, item) {
    return dispatch("callUpdateItem", item);
  },
  updateDiscount({ dispatch }, params) {
    const { quote_update_url, discountId } = params;

    return API.makeRequest(quote_update_url, {
      method: "PUT",
      data: {
        discount_id: discountId
      }
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  updateQuoteMembershipProgram({ dispatch }, params) {
    const { quote, data } = params;
    return API.makeRequest(quote.links.quote_pricing, {
      method: "PUT",
      data
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  updateLateFee({ dispatch }, params) {
    const { quote, lateFee } = params;
    dispatch("confirmQuoteChange").then(() => {
      return API.makeRequest(quote.links.quote_update, {
        method: "PUT",
        data: { late_fee_cents: lateFee }
      }).then((response) => {
        dispatch("refreshJobAndQuote", response.data);
      });
    });
  },
  async updateQuoteDetails({ dispatch }, { link, data }) {
    const res = await API.makeRequest(link, { method: "PUT", data });
    dispatch("setCurrentQuote", res.data);
    return res.data;
  },
  updateQuote({ dispatch, state }, data) {
    return API.makeRequest(`${state.currentQuote.links.quote_update}`, {
      method: "PUT",
      data
    }).then((res) => {
      dispatch("refreshJobAndQuote", res.data);
    });
  },
  updateQuoteDisplayMembershipSavings(
    { dispatch },
    { url, display_membership_savings }
  ) {
    return API.makeRequest(url, {
      method: "PUT",
      data: { display_membership_savings }
    }).then((res) => {
      dispatch("refreshJobAndQuote", res.data);
    });
  },
  confirmQuoteChange({ commit, state }, quote) {
    return new Promise((resolve, reject) => {
      const currentQuote = state.currentQuote || quote;
      if (!currentQuote || currentQuote.status !== "approved") {
        //confirm status here
        return resolve(void 0);
      }
      commit("setupConfirm", { resolve, reject });
      commit("toggleConfirmChanges", {
        show: true,
        title: "Confirm",
        message:
          "If you continue, this quote will no longer have customer approval. \nA new customer signature will be required."
      });
    });
  },
  setQuoteState({ dispatch }, state_change_url) {
    return API.makeRequest(state_change_url, {
      method: "PUT"
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  cloneQuote({ dispatch }, clone_url) {
    return API.makeRequest(clone_url, {
      method: "POST"
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  declineQuote({ dispatch }, { decline_url, reason }) {
    return API.makeRequest(decline_url, {
      method: "POST",
      data: {
        reason
      }
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  setCurrentQuote({ commit }, quote) {
    commit("replace", {
      state: "currentQuote",
      data: quote
    });
  },
  async refreshCurrentQuote({ dispatch, state }) {
    if (!state.currentQuote) return;

    const response = await dispatch("fetchQuote", {
      quoteId: state.currentQuote.id,
      customerId: state.currentQuote.customer_id
    });
    dispatch("refreshJobAndQuote", response.data);
  },
  refreshJobAndQuote({ dispatch, state }, quote) {
    const jobId = state.job.id || quote.job_id;
    if (jobId) {
      //how to fetch job
      dispatch("fetchJob", {
        jobId
      });
    }
    if (state.currentQuote) {
      dispatch("setCurrentQuote", quote);
    }
  },
  async regroupItems({ dispatch }, { items, url }) {
    const response = await API.makeRequest(`${url}`, {
      method: "PUT",
      data: items
    });
    dispatch("refreshJobAndQuote", response.data);
  },
  unapproveQuoteSignature({ dispatch }, break_url) {
    return API.makeRequest(break_url, {
      method: "POST"
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  async getEmailRecipientsForQuote(
    { dispatch },
    params: {
      id: number;
      customer_id: number;
    }
  ) {
    const quoteResponse = await dispatch("fetchQuote", {
      quoteId: params.id,
      customerId: params.customer_id
    });
    const availableContactsResponse = await API.makeRequest<{
      emails: {
        required: {
          email_address: string;
          owner: string;
          label: string | null;
          checked: boolean;
        }[];
        optional: {
          email_address: string;
          owner: string;
          label: string | null;
          checked: boolean;
        }[];
      };
    }>(
      `/v2/customers/${params.customer_id}/addresses/${quoteResponse.data.service_address_id}/contact`
    );
    return [
      ...sortBy(
        availableContactsResponse.data.emails.required.map((email) => {
          return new EmailRecipient({
            owner: email.owner as any,
            primary: true,
            name: email.label || "",
            email_address: email.email_address,
            checked: email.checked
          });
        }),
        "email"
      ),
      ...sortBy(
        availableContactsResponse.data.emails.optional.map((email) => {
          return new EmailRecipient({
            owner: email.owner as any,
            primary: false,
            name: email.label || "",
            email_address: email.email_address,
            checked: email.checked
          });
        }),
        "email"
      )
    ] as EmailRecipient[];
  },
  async emailQuote(
    _,
    params: {
      link: string;
      email_addresses?: string[];
      customizations?: EmailTemplateCustomization[];
    }
  ) {
    return API.makeRequest(params.link, {
      method: "POST",
      data: {
        additional_emails: params.email_addresses || [],
        customizations: (params.customizations || []).map((customization) => ({
          key: customization.key,
          value: customization.value
        }))
      }
    });
  }
};
